import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { ChatMessage, ChatSession, Tool } from '../types';
import { AgentDomain, DEFAULT_DOMAIN } from '../utils/agentCatalog';

interface ChatContextType {
  messages: ChatMessage[];
  currentSession: ChatSession | null;
  /**
   * The domain (appraisal / legal / financial) of the active conversation.
   * Drives the chat avatar + greeting and the `domain` sent with each message.
   * Set when opening an existing chat or starting a new one in a domain;
   * persists across `clearChat` so a "+ שיחה חדשה" inherits the picked domain.
   */
  activeDomain: AgentDomain;
  setActiveDomain: (d: AgentDomain) => void;
  /**
   * The server-assigned id of the in-flight chat session. Lives in the
   * context (not in ChatArea local state) because the new URL routing in
   * /c/:chatId needs to drive the same value the composer reads when
   * sending the next message.
   */
  currentSessionId: string | null;
  isStreaming: boolean;
  selectedTools: Tool[];
  fileContext: string[];
  addMessage: (message: ChatMessage) => void;
  updateMessage: (id: string, updates: Partial<ChatMessage>) => void;
  setMessages: (messages: ChatMessage[]) => void;
  setCurrentSession: (session: ChatSession | null) => void;
  setCurrentSessionId: (id: string | null) => void;
  setIsStreaming: (streaming: boolean) => void;
  setSelectedTools: (tools: Tool[]) => void;
  setFileContext: (files: string[]) => void;
  clearChat: () => void;
  abortController: React.MutableRefObject<AbortController | null>;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentSession, setCurrentSession] = useState<ChatSession | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [selectedTools, setSelectedTools] = useState<Tool[]>([]);
  const [fileContext, setFileContext] = useState<string[]>([]);
  const [activeDomain, setActiveDomain] = useState<AgentDomain>(DEFAULT_DOMAIN);
  const abortController = useRef<AbortController | null>(null);

  const addMessage = useCallback((message: ChatMessage) => {
    setMessages(prev => [...prev, message]);
  }, []);

  const updateMessage = useCallback((id: string, updates: Partial<ChatMessage>) => {
    setMessages(prev =>
      prev.map(msg => (msg.id === id ? { ...msg, ...updates } : msg))
    );
  }, []);

  const clearChat = useCallback(() => {
    setMessages([]);
    setCurrentSession(null);
    setCurrentSessionId(null);
    setFileContext([]);
  }, []);

  return (
    <ChatContext.Provider
      value={{
        messages,
        currentSession,
        currentSessionId,
        activeDomain,
        setActiveDomain,
        isStreaming,
        selectedTools,
        fileContext,
        addMessage,
        updateMessage,
        setMessages,
        setCurrentSession,
        setCurrentSessionId,
        setIsStreaming,
        setSelectedTools,
        setFileContext,
        clearChat,
        abortController,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};
